import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  Smartphone, 
  Cloud, 
  Brain, 
  Building2, 
  ShieldCheck, 
  Server 
} from 'lucide-react';

const ExperienceSection = () => {
  const { t } = useLanguage();

  const areas = [
    { key: 'mobile', icon: Smartphone, label: t.experience.areas.mobile, desc: t.experience.areas.mobileDesc },
    { key: 'cloud', icon: Cloud, label: t.experience.areas.cloud, desc: t.experience.areas.cloudDesc },
    { key: 'ai', icon: Brain, label: t.experience.areas.ai, desc: t.experience.areas.aiDesc },
    { key: 'financial', icon: Building2, label: t.experience.areas.financial, desc: t.experience.areas.financialDesc },
    { key: 'security', icon: ShieldCheck, label: t.experience.areas.security, desc: t.experience.areas.securityDesc },
    { key: 'highAvail', icon: Server, label: t.experience.areas.highAvail, desc: t.experience.areas.highAvailDesc },
  ];

  return (
    <section id="experience" className="py-32 lg:py-40 bg-zinc-950 relative overflow-hidden">
      {/* Large background text */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[20vw] font-extralight text-zinc-900/50 whitespace-nowrap select-none pointer-events-none">
        20+
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-8">
          {/* Left Content */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent" />
              <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium">Expertise</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-extralight text-white mb-8 leading-tight">
              {t.experience.title}
            </h2>
            <p className="text-lg text-zinc-500 font-light leading-relaxed mb-16">
              {t.experience.subtitle}
            </p>
            
            {/* Stats with dramatic styling */}
            <div className="relative inline-block">
              <div className="text-6xl lg:text-7xl font-extralight text-white mb-2 tracking-tight">
                20<span className="text-cyan-400">+</span>
              </div>
              <div className="text-xs text-zinc-500 uppercase tracking-[0.2em]">Years</div>
              <div className="absolute -bottom-4 left-0 w-full h-px bg-gradient-to-r from-zinc-800 to-transparent" />
            </div>
          </div>

          {/* Right Content - Expertise Areas */}
          <div className="lg:col-span-7 lg:pl-8">
            <div className="space-y-1">
              {areas.map(({ key, icon: Icon, label, desc }, index) => (
                <div 
                  key={key}
                  className="group relative flex items-start gap-6 p-6 bg-black/30 border-l-2 border-zinc-900 hover:border-cyan-500/50 hover:bg-zinc-900/30 transition-all duration-500"
                >
                  {/* Number indicator */}
                  <div className="absolute top-6 right-6 text-xs text-zinc-700 font-mono">
                    0{index + 1}
                  </div>
                  
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-zinc-600 group-hover:text-cyan-400 transition-colors duration-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg text-white font-medium mb-1 group-hover:text-cyan-50 transition-colors">{label}</h3>
                    <p className="text-sm text-zinc-500 group-hover:text-zinc-400 transition-colors">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
